import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Box, Button, Tabs, Tab, Typography } from "@mui/material";
import { isCoffeeOnlyMember } from "../../utils/coffeePass";
import SalesDashboard from "../SalesDashboard";
import { api } from "../../utils/api";
import MerchantStoreMap from "../Locations/MerchantStoreMap";
import ContractAcceptanceModal from "../../components/ContractAcceptanceModal";
import NoticeBanner from "../../components/NoticeBanner";
import {
  subscriptionRequiresContract,
  isLinkedAddOnSubscription,
} from "../../utils/subscriptionContractGate";

const Dashboard = () => {
  const currentPortal = sessionStorage.getItem("selectedPortal");

  const [loading, setLoading] = useState(false);

  const [productType, setProductType] = useState("");
  const [activeSubscriptionCount, setActiveSubscriptionCount] = useState(0);

  const [tabValue, setTabValue] = useState(0);

  // Contract acceptance modal state for customers
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [pendingContractsQueue, setPendingContractsQueue] = useState([]);
  // null until the member's subscriptions load; coffee-only members do not get the wash map
  const [coffeeOnlyMember, setCoffeeOnlyMember] = useState(null);

  // Notice state
  const [notices, setNotices] = useState([]);
  const [dismissedNotices, setDismissedNotices] = useState([]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const fetchActiveSubscriptions = async () => {
      try {
        setLoading(true);
        const payload = productType ? { productType } : {};

        const response = await api("v1/subscription/getAllActiveSubscriptions", "POST", payload);
        if (response.data.success) {
          setActiveSubscriptionCount(response.data.data?.activeSubscriptionsCount ?? "N/A");
        }
      } catch (error) {
        console.error("API error:", error);
      } finally {
        setLoading(false);
      }
    };

    if (currentPortal === "Administrator") {
      fetchActiveSubscriptions();
    }
  }, [productType, currentPortal]);

  useEffect(() => {
    setTabValue(0);
  }, [currentPortal]);

  // Check for pending contracts when customer loads dashboard
  useEffect(() => {
    if (currentPortal === "Customer") {
      checkForPendingContracts();
    }
  }, [currentPortal]);

  // Fetch notices for merchant portal
  useEffect(() => {
    const fetchNotices = async () => {
      if (currentPortal === "Merchant") {
        try {
          const response = await api("v1/notice/portal/merchant", "GET");
          console.log("Notices API response:", {
            status: response?.status,
            success: response?.data?.success,
            noticesCount: response?.data?.data?.notices?.length || 0,
            error: response?.data?.message
          });

          if (response && response.data && response.data.success && response.data.data.notices) {
            console.log("Found notices:", response.data.data.notices.length);
            setNotices(response.data.data.notices);
          } else {
            console.log("No notices found or invalid response structure", response?.data);
            setNotices([]);
          }
        } catch (error) {
          console.error("Error fetching notices:", error);
          console.error("Error details:", error.response?.data || error.message);
          setNotices([]);
        }
      } else {
        setNotices([]);
      }
    };

    fetchNotices();
  }, [currentPortal]);

  // Handle notice dismissal
  const handleDismissNotice = (noticeId) => {
    setDismissedNotices([...dismissedNotices, noticeId]);
  };

  // Filter out dismissed notices
  const visibleNotices = notices.filter((notice) => !dismissedNotices.includes(notice._id));

  // Check for pending contracts across all customer subscriptions
  const checkForPendingContracts = async () => {
    try {
      const currentUser = JSON.parse(sessionStorage.getItem("currentUser"));
      if (!currentUser) {
        console.log("No current user found for contract check");
        setCoffeeOnlyMember(false);
        return;
      }

      console.log("🔍 Checking for pending contracts for user:", currentUser._id);

      // Fetch customer subscriptions
      const response = await api(`v1/subscription/customer/${currentUser._id}`, "GET");

      if (response.data.success && response.data.data.allSubscriptions) {
        const subscriptions = response.data.data.allSubscriptions;
        console.log("📋 Found subscriptions:", subscriptions.length);
        setCoffeeOnlyMember(isCoffeeOnlyMember(subscriptions));

        // Array to collect all pending contracts
        const pendingContracts = [];

        // Only check for contracts if customer has subscriptions
        if (subscriptions.length > 0) {
          // Check each subscription for pending contracts
          for (const subscription of subscriptions) {
            console.log("🔎 Checking subscription:", subscription._id, "Product type:", subscription.product?.type);

            if (isLinkedAddOnSubscription(subscription)) {
              console.log("⏭️ Linked add-on uses main contract — skip duplicate modal");
              continue;
            }

            if (subscriptionRequiresContract(subscription.product, subscription)) {
              console.log("✅ Contract-gated product, checking contract requirement...");

              const contractResponse = await api(`v1/contract/check-required/${subscription._id}`, "GET");
              console.log("📄 Contract required response:", contractResponse.data);

              if (contractResponse.data.success && contractResponse.data.data.contractAcceptanceRequired) {
                console.log("✅ Contract acceptance required, checking for pending contract...");

                // Check if there's a pending contract
                const pendingResponse = await api(`v1/contract/check-pending/${subscription._id}`, "GET");
                console.log("⏳ Pending contract response:", pendingResponse.data);

                if (pendingResponse.data.success && pendingResponse.data.data) {
                  console.log("🎯 Found pending contract for subscription:", subscription._id);
                  // Add to pending contracts queue
                  pendingContracts.push({
                    contractData: pendingResponse.data.data,
                    subscription: subscription
                  });
                } else {
                  console.log("❌ No pending contract found");
                }
              } else {
                console.log("❌ Contract acceptance not required or API error");
              }
            } else {
              console.log("⏭️ Skipping product without contract requirement");
            }
          }
        } else {
          console.log("📭 No subscriptions found");
        }

        // Store all pending contracts in queue
        if (pendingContracts.length > 0) {
          console.log(`📝 Found ${pendingContracts.length} pending contract(s) to sign`);
          setPendingContractsQueue(pendingContracts);
          setContractModalOpen(true);
        } else {
          console.log("✅ No pending contracts found");
          setPendingContractsQueue([]);
        }
      } else {
        console.log("❌ Failed to fetch subscriptions or no subscriptions in response");
        setCoffeeOnlyMember(false);
      }
    } catch (error) {
      console.error("💥 Error checking for pending contracts:", error);
      setCoffeeOnlyMember(false);
    }
  };

  const handleContractAccepted = () => {
    console.log("✅ All pending contracts signed in session");
    setContractModalOpen(false);
    setPendingContractsQueue([]);
  };

  const handleContractModalClose = () => {
    setContractModalOpen(false);
    setTimeout(() => checkForPendingContracts(), 300);
  };

  const handleProductTypeChange = (event) => {
    const selectedProductType = event.target.value;
    setProductType(selectedProductType);
  };

  if (currentPortal === "Customer") {
    return (
      <div className="flex w-full flex-1 flex-col">
        <div className="flex w-full flex-1 flex-col justify-center mx-auto text-center">
          {coffeeOnlyMember === true && (
            <Box className="flex flex-column items-center gap-3 p-6">
              <Typography variant="h5">Your Coffee Pass</Typography>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                Open your pass at a participating cafe and show the live QR code to the staff.
              </Typography>
              <Button component={Link} to="/my-subscriptions" variant="contained">
                Open my Coffee Pass
              </Button>
            </Box>
          )}
          {coffeeOnlyMember === false && <MerchantStoreMap />}
        </div>

        {/* Contract Acceptance Modal for Customers */}
        <ContractAcceptanceModal
          isOpen={contractModalOpen && pendingContractsQueue.length > 0}
          onClose={handleContractModalClose}
          pendingContracts={pendingContractsQueue}
          onContractAccepted={handleContractAccepted}
        />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-center mx-auto text-center flex-column w-full">
        {/* Display notices for Merchant portal */}
        {currentPortal === "Merchant" && visibleNotices.length > 0 && (
          <Box sx={{ padding: 2, maxWidth: "1200px", margin: "0 auto", width: "100%" }}>
            {visibleNotices.map((notice) => (
              <NoticeBanner key={notice._id} notice={notice} onDismiss={() => handleDismissNotice(notice._id)} />
            ))}
          </Box>
        )}
        {sessionStorage.getItem("selectedPortal") === "Administrator" && (
          <Box sx={{ marginTop: "22px", marginBottom: "12px" }}>
            <h2>Dashboard</h2>
            <div className="flex flex-column gap-2 justify-center items-center m-4 p-4">
              <select
                className="dropdown block max-w-[300px] p-2 rounded-lg text-gray-900 border focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                id="productType"
                onChange={(event) => handleProductTypeChange(event)}
              >
                <option value="">All Product Types</option>
                <option value="subscription">Subscription (Unlimited Redemptions)</option>
                <option value="package">Package (Limited Redemptions with Add-ons)</option>
                <option value="onceoff">One-Time Purchase</option>
              </select>
              <strong>
                Total Active Subscriptions Count: {loading ? "Loading…" : activeSubscriptionCount}
              </strong>
            </div>
          </Box>
        )}

        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="dashboard tabs">
              {(currentPortal === "Administrator" || currentPortal === "Partner") && <Tab label="Transactions" />}
              {(currentPortal === "Administrator" || currentPortal === "Merchant") && <Tab label="Redemptions" />}
            </Tabs>
          </Box>
          {(currentPortal === "Administrator" || currentPortal === "Partner") && (
            <TabPanel value={tabValue} index={0}>
              <h1>Transactions</h1>
              <SalesDashboard dataSource="transactions" />
            </TabPanel>
          )}
          {(currentPortal === "Administrator" || currentPortal === "Merchant") && (
            <TabPanel value={tabValue} index={currentPortal === "Merchant" ? 0 : 1}>
              <h1>Redemptions</h1>
              <SalesDashboard dataSource="redemptions" />
            </TabPanel>
          )}
        </Box>
      </div>
    </div>
  );
};

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </Typography>
  );
};

export default Dashboard;
